import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';

import UploadAndManageCSVFile from './UploadAndManageCSVFile';
import InputTargetConcentration from './InputTargetConcentration';

import {
  setCsvDelimiter,
  setNormalizationCurrentVolume,
  setTargetConcentration,
  setWellIncrementPattern,
} from '../../../stores/actions/normalizationStateAction';
import {
  setAndGeneratePreviewData,
  selectCSVRowColumnIndex,
  uploadNormalizationCSVFile,
} from '../../../stores/business/taskListNormalizationBusinessAction';
import * as AppStateAction from '../../../stores/actions/appStateAction';

import './NormalizationWizardView.scss';
import {globalAddUndoItem, globalUndoRedoUpdatePresentState} from '../../../stores/business/undoRedoBusinessAction';

function NormalizationWizardView({
  show,
  normalizationStateActionSetWellIncrementPattern,
  normalizationStateActionSetCsvDelimiter,
  normalizationStateActionSetTargetConcentration,
  normalizationStateActionSetNormalizationCurrentVolume,
  taskListNormalizationUploadNormalizationCSVFile,
  taskListNormalizationGeneratePreviewData,
  taskListNormalizationSelectCSVRowColumnIndex,
  undoRedoBusinessActionGlobalAddUndoItem,
  undoRedoBusinessActionGlobalUndoRedoUpdatePresentState,
  appStateAction,
}) {
  const {normalizationState} = useSelector((state) => state);
  const [targetConcentrationChanged, setTargetConcentrationChanged] = useState(false);
  const [currentVolumeChanged, setCurrentVolumeChanged] = useState(false);
  const [initalCurrentVolume, setInitalCurrentVolume] = useState(normalizationState.currentVolume);

  useEffect(() => {
    setTargetConcentrationChanged(false);
    setCurrentVolumeChanged(false);

    if (normalizationState.currentWizardView === 4) {
      setInitalCurrentVolume(normalizationState.currentVolume);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizationState.currentWizardView]);

  useEffect(() => {
    if (normalizationState.targetConcentration === 0) {
      setTargetConcentrationChanged(false);
    }

    if (normalizationState.currentVolume === initalCurrentVolume) {
      setCurrentVolumeChanged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizationState.targetConcentration, normalizationState.currentVolume]);

  const handleChangeWellIncrementPattern = (value) => {
    normalizationStateActionSetWellIncrementPattern(value);
  };

  const handleChangeTargetConcentration = (value) => {
    const numValue = Number.isNaN(parseFloat(value)) ? 0 : parseFloat(value);
    const targetConcentrationValue = Math.round(numValue * 10) / 10;

    const {targetConcentration} = normalizationState;

    if (!targetConcentrationChanged && targetConcentration !== targetConcentrationValue) {
      setTargetConcentrationChanged(true);
      setCurrentVolumeChanged(false);
      undoRedoBusinessActionGlobalAddUndoItem();
    }

    normalizationStateActionSetTargetConcentration(targetConcentrationValue);
    const previewData = taskListNormalizationGeneratePreviewData();

    if (targetConcentrationChanged && targetConcentrationValue !== 0) {
      undoRedoBusinessActionGlobalUndoRedoUpdatePresentState('normalizationState', {
        targetConcentration: targetConcentrationValue,
        previewData,
      });
    }
  };

  const handleChangeCurrentVolume = (value) => {
    const numValue = Number.isNaN(parseFloat(value)) ? 0 : parseFloat(value);
    const {currentVolume} = normalizationState;

    if (!currentVolumeChanged && currentVolume !== numValue) {
      setCurrentVolumeChanged(true);
      setTargetConcentrationChanged(false);
      undoRedoBusinessActionGlobalAddUndoItem();
    }
    normalizationStateActionSetNormalizationCurrentVolume(numValue);
    const previewData = taskListNormalizationGeneratePreviewData();

    if (currentVolumeChanged && numValue !== initalCurrentVolume) {
      undoRedoBusinessActionGlobalUndoRedoUpdatePresentState('normalizationState', {
        currentVolume: numValue,
        previewData,
      });
    }
  };

  const getContent = () => {
    let content;
    switch (normalizationState.currentWizardView) {
      case 3:
        content = (
          <UploadAndManageCSVFile
            handleChangeWellIncrementPattern={handleChangeWellIncrementPattern}
            onUploadNormalizationCSVFile={taskListNormalizationUploadNormalizationCSVFile}
            handleChangeDelimiter={normalizationStateActionSetCsvDelimiter}
            handleSelectRowColumnIndex={taskListNormalizationSelectCSVRowColumnIndex}
            appStateAction={appStateAction}
            globalAddUndoItem={undoRedoBusinessActionGlobalAddUndoItem}
          />
        );
        break;
      case 4:
        content = (
          <InputTargetConcentration
            handleChangeTargetConcentration={handleChangeTargetConcentration}
            handleChangeCurrentVolume={handleChangeCurrentVolume}
          />
        );
        break;
      default:
        break;
    }
    return content;
  };
  return (
    <div className={`normalization-wizard-view ${show ? 'protocol-editor__div__content-wizard' : ''}`}>
      {getContent()}
    </div>
  );
}

NormalizationWizardView.propTypes = {
  show: PropTypes.bool,
  normalizationStateActionSetWellIncrementPattern: PropTypes.func.isRequired,
  normalizationStateActionSetCsvDelimiter: PropTypes.func.isRequired,
  normalizationStateActionSetTargetConcentration: PropTypes.func.isRequired,
  normalizationStateActionSetNormalizationCurrentVolume: PropTypes.func.isRequired,
  taskListNormalizationUploadNormalizationCSVFile: PropTypes.func.isRequired,
  taskListNormalizationGeneratePreviewData: PropTypes.func.isRequired,
  taskListNormalizationSelectCSVRowColumnIndex: PropTypes.func.isRequired,
  undoRedoBusinessActionGlobalAddUndoItem: PropTypes.func.isRequired,
  undoRedoBusinessActionGlobalUndoRedoUpdatePresentState: PropTypes.func.isRequired,
  appStateAction: PropTypes.object.isRequired,
};

NormalizationWizardView.defaultProps = {
  show: false,
};

function mapDispatchToProps(dispatch) {
  return {
    normalizationStateActionSetWellIncrementPattern: bindActionCreators(setWellIncrementPattern, dispatch),
    normalizationStateActionSetCsvDelimiter: bindActionCreators(setCsvDelimiter, dispatch),
    normalizationStateActionSetTargetConcentration: bindActionCreators(setTargetConcentration, dispatch),
    normalizationStateActionSetNormalizationCurrentVolume: bindActionCreators(setNormalizationCurrentVolume, dispatch),
    taskListNormalizationUploadNormalizationCSVFile: bindActionCreators(uploadNormalizationCSVFile, dispatch),
    taskListNormalizationGeneratePreviewData: bindActionCreators(setAndGeneratePreviewData, dispatch),
    taskListNormalizationSelectCSVRowColumnIndex: bindActionCreators(selectCSVRowColumnIndex, dispatch),
    appStateAction: bindActionCreators(AppStateAction, dispatch),
    undoRedoBusinessActionGlobalAddUndoItem: bindActionCreators(globalAddUndoItem, dispatch),
    undoRedoBusinessActionGlobalUndoRedoUpdatePresentState: bindActionCreators(
      globalUndoRedoUpdatePresentState,
      dispatch,
    ),
  };
}

export default connect(null, mapDispatchToProps)(NormalizationWizardView);
